import dayjs, { Dayjs } from 'dayjs';
import { memo } from 'react';

import { Button, Flex, Group, Select, Text, TextInput } from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { isNotEmpty, useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';

import CarAccessInfo from '@/components/car-access-info/car-access-info';

import { useLazyCheckCarAccessQuery } from '@/api/bookings';
import { useGetAllObjectsQuery } from '@/api/objects';

import { LayoutRow } from '@/shared/uikit/layouts/LayoutRow';

import { IconX } from '@tabler/icons-react';

interface FormData {
	location_id: number | null;
	direction: string | null;
	date: Dayjs | null;
	car_number: string;
}

function CarAccess() {
	const form = useForm({
		initialValues: {
			location_id: null,
			car_number: '',
			direction: null,
			date: null,
		},
		validate: {
			location_id: isNotEmpty('Выберите объект'),
			car_number: (value) => {
				return value === '' || value.length < 4 || value.length > 10
					? 'Введите номер авто от 4 до 10 символов'
					: null;
			},
			direction: isNotEmpty('Выберите направление'),
			date: isNotEmpty('Выберите дату и время'),
		},
	});

	const { data: objects } = useGetAllObjectsQuery();
	const [checkCarAccess, { data: checkResponse }] = useLazyCheckCarAccessQuery();

	const handleCheck = async (data: FormData) => {
		const { date, location_id, direction, car_number } = data;

		if (!location_id || !direction || !date) return;

		try {
			await checkCarAccess({
				date: date.toISOString(),
				location_id,
				direction,
				car_number,
			});
		} catch (e) {
			notifications.show({
				title: 'Ошибка',
				message: 'Не удалось запросить информацию о проезде',
				color: 'red',
				icon: <IconX />,
			});
		}
	};

	return (
		<>
			<LayoutRow
				title={{
					title: { children: 'Право проезда', size: 'h4', fw: 800 },
				}}
				type="once"
				style={{ height: 'calc(100vh - 116px)' }}
			>
				<form
					onSubmit={form.onSubmit((values) => {
						handleCheck(values);
					})}
				>
					<Flex direction="column" gap="md">
						<Select
							label="Объект"
							data={
								objects?.objects.map((elem) => {
									return { value: `${elem.id}`, label: elem.name };
								}) ?? []
							}
							limit={30}
							placeholder="Парковка №19"
							allowDeselect={false}
							searchable
							withAsterisk
							{...form.getInputProps('location_id')}
						/>
						<TextInput
							label="Номер авто"
							placeholder="A123MP199"
							withAsterisk
							{...form.getInputProps('car_number')}
						></TextInput>
						<Select
							label="Направление"
							allowDeselect={false}
							withAsterisk
							data={[
								{ value: 'in', label: 'Въезд' },
								{ value: 'out', label: 'Выезд' },
							]}
							placeholder="Въезд"
							{...form.getInputProps('direction')}
						/>
						<DateTimePicker
							maw="150px"
							label="Дата"
							radius="lg"
							placeholder="Выберите дату"
							valueFormat="DD.MM.YYYY HH:mm"
							minDate={dayjs().toDate()}
							maxDate={dayjs().add(1, 'month').toDate()}
							withAsterisk
							clearable
							{...form.getInputProps('date')}
						/>
						<Group gap="md">
							<Button variant="filled" radius="lg" type="submit">
								<Text fw={500}>Проверить</Text>
							</Button>
							<Button variant="light" radius="lg" onClick={() => form.reset()}>
								Сбросить
							</Button>
						</Group>
					</Flex>
				</form>
				{checkResponse && <CarAccessInfo data={{ ...checkResponse }} />}
			</LayoutRow>
		</>
	);
}

export default memo(CarAccess);
